import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { useApp } from "../context/AppContext";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { toast } from "./ui/sonner";
import { ArrowLeftRight, Plus, Trash2, Building2, Loader2, Settings2, BellRing, TrendingUp, FileSpreadsheet, AlertTriangle } from "lucide-react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";
import { fmtD } from "../lib/date";
import { FxRatePanel } from "./finance/FxRatePanel";

const money = (n) => "$" + Number(n || 0).toLocaleString();
const cny = (n) => "¥" + Number(n || 0).toLocaleString();
const inp = "mt-1 w-full text-sm rounded-md border border-slate-200 px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-400";

export default function IntercompanyFees({ zh }) {
  const { api, user } = useApp();
  const [d, setD] = useState(null);
  const [month, setMonth] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showCats, setShowCats] = useState(false);
  const [dash, setDash] = useState(null);
  const canManage = ["admin", "finance", "ceo"].includes(user.role);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/finance/intercompany-fees", { params: month ? { month } : {} }); setD(data); }
    catch { setD({ items: [], categories: [], payers: [], summary: { total: 0, count: 0, byPayer: [], byCategory: [] } }); }
  }, [api, month]);
  useEffect(() => { load(); }, [load]);
  const loadDash = useCallback(async () => {
    try { const { data } = await api.get("/finance/cd-dashboard"); setDash(data); } catch {}
  }, [api]);
  useEffect(() => { loadDash(); }, [loadDash]);

  const sendReminder = async () => {
    try {
      const { data } = await api.post("/finance/intercompany-fee-reminder");
      if (data.skipped) toast.info(zh ? `${data.month} 的录入提醒本月已发送过（每月初也会自动发送）` : `Reminder for ${data.month} already sent`);
      else toast.success(zh ? `已提醒财务录入 ${data.month} 服务费（${data.created} 条提醒${(data.missing || []).length ? `，未录入：${data.missing.join("、")}` : ""}）` : `Sent ${data.created} reminders`);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  const del = async (fid) => {
    if (!window.confirm(zh ? "删除该服务费记录？将同时删除对应的分公司支出与成都收入分录。" : "Delete this fee and its linked entries?")) return;
    try { await api.delete(`/finance/intercompany-fees/${fid}`); toast.success(zh ? "已删除（含双向分录）" : "Deleted"); load(); loadDash(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  if (!d) return <div className="text-sm text-slate-400 py-10 text-center" data-testid="icf-loading"><Loader2 className="h-4 w-4 animate-spin inline mr-1" />{zh ? "加载中…" : "Loading…"}</div>;
  const s = d.summary || {};
  const catLbl = (k) => { const c = (d.categories || []).find((x) => x.key === k); return c ? (zh ? c.zh : c.en) : k; };
  const payerLbl = (k) => { const p = (d.payers || []).find((x) => x.key === k); return p ? (zh ? p.zh : p.en) : k; };

  return (
    <div className="space-y-4" data-testid="intercompany-fees">
      <div className="bg-teal-50 border border-teal-200 rounded-xl px-4 py-3 text-sm text-teal-900 flex items-start gap-2" data-testid="icf-explainer">
        <ArrowLeftRight className="h-4 w-4 mt-0.5 shrink-0 text-teal-600" />
        <p>{zh
          ? "成都公司（全资子公司）为美国各分公司提供内部支持服务，账套以 ¥ 人民币记账。录入服务费（$）后自动双向记账：付费分公司计入运营费用「内部支持服务费(成都)」，成都按当日汇率折 ¥ 计入收入「内部服务费收入」；汇总到总表时再按各记账日汇率折算美元。内部交易在合并利润层面自动抵消。"
          : "Chengdu Co. (wholly-owned) provides internal support to US branches and keeps its books in CNY. Each fee ($) auto-books both sides: payer branch operating expense + Chengdu service income converted at that day's rate. Consolidated totals use the USD equivalent."}</p>
      </div>

      {dash && <CdDashboard zh={zh} dash={dash} api={api} canManage={canManage} onSaved={loadDash} />}

      <FxRatePanel zh={zh} canManage={canManage} onChanged={loadDash} />

      <FixedExpenseCard zh={zh} api={api} canManage={canManage} onGenerated={loadDash} />

      <div className="flex flex-wrap items-center gap-2">
        <input type="month" data-testid="icf-month-filter" value={month} onChange={(e) => setMonth(e.target.value)}
          className="text-sm rounded-md border border-slate-200 px-2.5 py-1.5 bg-white" />
        {month && <button data-testid="icf-month-clear" onClick={() => setMonth("")} className="text-xs text-slate-500 hover:text-red-600 underline">{zh ? "清除月份" : "Clear"}</button>}
        <span className="text-xs text-slate-400">{month ? (zh ? `仅显示 ${month}` : month) : (zh ? "全部期间" : "All periods")}</span>
        {canManage && (
          <>
            <button data-testid="icf-reminder-btn" onClick={sendReminder}
              className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-md border border-slate-300 text-slate-600 hover:border-amber-500 hover:text-amber-600 transition-colors bg-white">
              <BellRing className="h-3.5 w-3.5" />{zh ? "发送录入提醒" : "Send Reminder"}
            </button>
            <button data-testid="icf-manage-cats-btn" onClick={() => setShowCats(true)}
              className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-md border border-slate-300 text-slate-600 hover:border-teal-500 hover:text-teal-600 transition-colors bg-white">
              <Settings2 className="h-3.5 w-3.5" />{zh ? "管理类别" : "Categories"}
            </button>
            <Button size="sm" data-testid="icf-new-btn" className="bg-teal-600 hover:bg-teal-700" onClick={() => setShowNew(true)}>
              <Plus className="h-4 w-4 mr-1" />{zh ? "录入服务费" : "New Fee"}
            </Button>
          </>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="icf-summary">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-[11px] text-slate-400">{zh ? "服务费总额（成都收入）" : "Total fees (Chengdu income)"}</p>
          <p className="text-2xl font-extrabold text-teal-700 mt-1" data-testid="icf-total">{money(s.total)}</p>
          <p className="text-[11px] text-slate-400">{s.count} {zh ? "笔" : "records"}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-[11px] text-slate-400 mb-1.5">{zh ? "按付费分公司" : "By payer branch"}</p>
          <div className="flex flex-wrap gap-1.5" data-testid="icf-by-payer">
            {(s.byPayer || []).length === 0 && <span className="text-sm text-slate-300">—</span>}
            {(s.byPayer || []).map((x) => (
              <span key={x.key} className="text-[11px] px-2 py-1 rounded-full bg-slate-100 text-slate-700 font-medium">{x.label} · {money(x.amount)}</span>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-[11px] text-slate-400 mb-1.5">{zh ? "按服务类别" : "By service category"}</p>
          <div className="flex flex-wrap gap-1.5" data-testid="icf-by-category">
            {(s.byCategory || []).length === 0 && <span className="text-sm text-slate-300">—</span>}
            {(s.byCategory || []).map((x) => (
              <span key={x.key} className="text-[11px] px-2 py-1 rounded-full bg-teal-50 text-teal-700 font-medium">{x.label} · {money(x.amount)}</span>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="icf-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="text-left font-medium px-4 py-2.5">{zh ? "日期" : "Date"}</th>
              <th className="text-left font-medium px-3 py-2.5">{zh ? "付费方" : "Payer"}</th>
              <th className="text-left font-medium px-3 py-2.5">{zh ? "服务类别" : "Category"}</th>
              <th className="text-left font-medium px-3 py-2.5">{zh ? "说明" : "Description"}</th>
              <th className="text-right font-medium px-3 py-2.5">{zh ? "金额" : "Amount"}</th>
              <th className="text-left font-medium px-3 py-2.5">{zh ? "录入人" : "By"}</th>
              <th className="px-3 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {d.items.length === 0 && <tr><td colSpan={7} className="text-center text-slate-300 py-10">{zh ? "暂无内部服务费记录" : "No records"}</td></tr>}
            {d.items.map((x) => (
              <tr key={x.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`icf-row-${x.id}`}>
                <td className="px-4 py-2.5 text-slate-700 whitespace-nowrap">{fmtD(x.date)}</td>
                <td className="px-3 py-2.5 font-medium text-slate-800 whitespace-nowrap"><span className="inline-flex items-center gap-1"><Building2 className="h-3.5 w-3.5 text-slate-400" />{payerLbl(x.fromBranch)}</span></td>
                <td className="px-3 py-2.5"><span className="text-[11px] px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 font-medium whitespace-nowrap">{catLbl(x.category)}</span></td>
                <td className="px-3 py-2.5 text-slate-500 max-w-[320px]"><p className="truncate" title={x.description}>{x.description || "—"}</p></td>
                <td className="px-3 py-2.5 text-right font-semibold text-teal-700">{money(x.amount)}{x.amountCny != null && <span className="block text-[10px] font-normal text-slate-400" title={x.fxRate ? `1 USD = ${x.fxRate} CNY` : ""}>{zh ? "成都入账" : "CD"} {cny(x.amountCny)}</span>}</td>
                <td className="px-3 py-2.5 text-slate-400 text-xs whitespace-nowrap">{x.createdBy || "—"}</td>
                <td className="px-3 py-2.5 text-right">
                  {canManage && (
                    <button data-testid={`icf-del-${x.id}`} onClick={() => del(x.id)} className="text-slate-300 hover:text-red-600 transition-colors"><Trash2 className="h-4 w-4" /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showNew && <NewFeeDialog zh={zh} api={api} payers={d.payers} categories={d.categories} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); loadDash(); }} />}
      {showCats && <CategoryManager zh={zh} api={api} categories={d.categories} onClose={() => setShowCats(false)} onSaved={() => { setShowCats(false); load(); }} />}

      <AnnualReport zh={zh} api={api} />
    </div>
  );
}

function AnnualReport({ zh, api }) {
  const { user } = useApp();
  const nowY = new Date().getFullYear();
  const [year, setYear] = useState(nowY);
  const [d, setD] = useState(null);
  const [sending, setSending] = useState(false);
  const canSend = ["admin", "finance", "ceo"].includes(user.role);
  const sendEmail = async () => {
    if (!window.confirm(zh ? `将 ${year} 年度报表发送给管理层邮箱并通知管理层？（每年1月5日也会自动发送上一年报表）` : `Send ${year} report to management?`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/finance/intercompany-annual-email", { year });
      const okCnt = (data.results || []).filter((r) => r.ok).length;
      if ((data.recipients || []).length === 0) toast.info(zh ? "未配置管理层邮箱(REPORT_RECIPIENTS)，已发送站内通知" : "No recipients configured; in-app notice sent");
      else toast.success(zh ? `年度报表已发送 ${okCnt}/${data.recipients.length} 个管理层邮箱，并已发站内通知` : `Sent to ${okCnt}/${data.recipients.length}`);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setSending(false);
  };
  useEffect(() => {
    api.get("/finance/intercompany-annual", { params: { year } }).then(({ data }) => setD(data)).catch(() => setD(null));
  }, [api, year]);
  const money2 = (n) => "$" + Number(n || 0).toLocaleString();
  const exportXlsx = () => {
    if (!d) return;
    const cats = d.categories || [];
    const ws = XLSX.utils.json_to_sheet([
      ...d.rows.map((r) => ({
        [zh ? "分公司" : "Branch"]: r.branchLabel,
        ...Object.fromEntries(cats.map((c) => [zh ? c.zh : c.en, r.byCategory[c.key] || 0])),
        [zh ? "合计" : "Total"]: r.total,
      })),
      { [zh ? "分公司" : "Branch"]: zh ? "总计" : "Grand Total",
        ...Object.fromEntries(cats.map((c) => [zh ? c.zh : c.en, d.rows.reduce((s, r) => s + (r.byCategory[c.key] || 0), 0)])),
        [zh ? "合计" : "Total"]: d.grandTotal },
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `${year}`);
    const ws2 = XLSX.utils.json_to_sheet(d.monthly.map((m) => ({ [zh ? "月份" : "Month"]: m.month, [zh ? "金额" : "Amount"]: m.amount })));
    XLSX.utils.book_append_sheet(wb, ws2, zh ? "月度合计" : "Monthly");
    XLSX.writeFile(wb, `成都服务费年度汇总_${year}.xlsx`);
    toast.success(zh ? `已导出 ${year} 年度报表` : `Exported ${year} report`);
  };
  if (!d) return null;
  const cats = d.categories || [];
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="icf-annual">
      <div className="px-5 py-3 border-b border-slate-100 flex flex-wrap items-center gap-2">
        <FileSpreadsheet className="h-4 w-4 text-teal-600" />
        <h3 className="font-semibold text-slate-900">{zh ? "服务费年度汇总" : "Annual Fee Summary"}</h3>
        <select data-testid="icf-annual-year" className="text-sm rounded-md border border-slate-200 px-2 py-1 bg-white" value={year} onChange={(e) => setYear(Number(e.target.value))}>
          {[nowY, nowY - 1, nowY - 2].map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <span className="text-xs text-slate-400">{zh ? `${d.count} 笔 · 总计` : "Total"} <span className="font-bold text-teal-700" data-testid="icf-annual-total">{money2(d.grandTotal)}</span></span>
        <button data-testid="icf-annual-export" onClick={exportXlsx}
          className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-md border border-slate-300 text-slate-600 hover:border-emerald-500 hover:text-emerald-600 transition-colors bg-white">
          <FileSpreadsheet className="h-3.5 w-3.5" />{zh ? "导出 Excel" : "Export"}
        </button>
        {canSend && (
          <button data-testid="icf-annual-email" onClick={sendEmail} disabled={sending}
            className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-md bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white transition-colors">
            <BellRing className="h-3.5 w-3.5" />{sending ? (zh ? "发送中…" : "Sending…") : (zh ? "发送给管理层" : "Email Mgmt")}
          </button>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="text-left font-medium px-4 py-2.5 whitespace-nowrap">{zh ? "分公司" : "Branch"}</th>
              {cats.map((c) => <th key={c.key} className="text-right font-medium px-3 py-2.5 whitespace-nowrap">{zh ? c.zh : c.en}</th>)}
              <th className="text-right font-medium px-4 py-2.5">{zh ? "合计" : "Total"}</th>
            </tr>
          </thead>
          <tbody>
            {d.rows.map((r) => (
              <tr key={r.branch} className="border-t border-slate-100" data-testid={`icf-annual-row-${r.branch}`}>
                <td className="px-4 py-2.5 font-medium text-slate-800 whitespace-nowrap">{zh ? r.branchLabel : r.branchLabelEn}</td>
                {cats.map((c) => <td key={c.key} className={`px-3 py-2.5 text-right ${r.byCategory[c.key] ? "text-slate-700" : "text-slate-300"}`}>{r.byCategory[c.key] ? money2(r.byCategory[c.key]) : "—"}</td>)}
                <td className={`px-4 py-2.5 text-right font-semibold ${r.total ? "text-teal-700" : "text-slate-300"}`}>{r.total ? money2(r.total) : "—"}</td>
              </tr>
            ))}
            <tr className="border-t-2 border-slate-200 bg-slate-50/60">
              <td className="px-4 py-2.5 font-bold text-slate-900">{zh ? "总计" : "Grand Total"}</td>
              {cats.map((c) => {
                const v = d.rows.reduce((s, r) => s + (r.byCategory[c.key] || 0), 0);
                return <td key={c.key} className={`px-3 py-2.5 text-right font-semibold ${v ? "text-slate-800" : "text-slate-300"}`}>{v ? money2(v) : "—"}</td>;
              })}
              <td className="px-4 py-2.5 text-right font-bold text-teal-700">{money2(d.grandTotal)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CdDashboard({ zh, dash, api, canManage, onSaved }) {
  const nav = useNavigate();
  const [showBudget, setShowBudget] = useState(false);
  const netClr = (dash.totalNetCny ?? dash.totalNet) >= 0 ? "text-teal-700" : "text-red-600";
  const cm = dash.currentMonth || {};
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="cd-dashboard">
      {dash.totalExpense === 0 && (
        <div className="mb-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 flex flex-wrap items-center gap-2" data-testid="cd-cost-cta">
          <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
          <p className="text-sm text-amber-900 flex-1 min-w-[240px]">{zh
            ? "成都公司暂无运营成本记录：请在「运营费用」选择记账主体「成都公司」，以 ¥ 录入房租、工资、水电等成本，损益看板才能真实反映是否自负盈亏。"
            : "No Chengdu operating costs yet — enter rent, salaries, utilities under Operating with entity Chengdu so this P&L is meaningful."}</p>
          <button data-testid="cd-cost-cta-btn" onClick={() => nav("/finance-center?tab=operating&branch=cd")}
            className="text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded-md px-3 py-1.5 transition-colors">
            {zh ? "去录入成都成本 →" : "Enter costs →"}
          </button>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <TrendingUp className="h-4 w-4 text-teal-600" />
        <h3 className="font-semibold text-slate-900">{zh ? "成都公司 · 月度损益（¥）" : "Chengdu Co. · Monthly P&L (CNY)"}</h3>
        <span className="text-[11px] text-slate-400">{zh ? "近12个月 服务费收入 vs 运营成本(含折旧) · 人民币账套" : "Last 12 months: fee income vs operating cost · CNY books"}</span>
        <span data-testid="cd-dash-status" className={`ml-auto text-[11px] font-bold px-2.5 py-1 rounded-full ${dash.selfSufficient ? "bg-teal-100 text-teal-700" : "bg-red-100 text-red-700 animate-pulse"}`}>
          {dash.selfSufficient ? (zh ? "✓ 自负盈亏" : "Self-sufficient") : (zh ? "亏损·需补贴" : "Running at a loss")}
        </span>
        {canManage && (
          <button data-testid="cd-budget-btn" onClick={() => setShowBudget(true)}
            className="text-[11px] font-semibold px-2.5 py-1 rounded-md border border-slate-300 text-slate-600 hover:border-teal-500 hover:text-teal-600 transition-colors bg-white">
            {zh ? "设置预算/目标" : "Budget & Target"}
          </button>
        )}
      </div>

      {/* current month budget & target */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4" data-testid="cd-budget-panel">
        <BudgetBar zh={zh} kind="cost" label={zh ? `本月运营成本 (${cm.month || ""})` : "Cost this month"} value={cm.expenseCny || 0} limit={cm.budget || 0} bad={cm.overBudget} />
        <BudgetBar zh={zh} kind="target" label={zh ? `本月服务费收入 (${cm.month || ""})` : "Fee income this month"} value={cm.incomeCny || 0} limit={cm.target || 0} bad={cm.belowTarget} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="bg-teal-50/60 rounded-lg px-3 py-2"><p className="text-[11px] text-slate-400">{zh ? "服务费收入" : "Fee income"}</p><p className="text-lg font-extrabold text-teal-700" data-testid="cd-dash-income">{cny(dash.totalIncomeCny)}</p><p className="text-[10px] text-slate-400" data-testid="cd-dash-income-usd">≈ {money(dash.totalIncome)}</p></div>
        <div className="bg-amber-50/60 rounded-lg px-3 py-2"><p className="text-[11px] text-slate-400">{zh ? "运营成本" : "Operating cost"}</p><p className="text-lg font-extrabold text-amber-600" data-testid="cd-dash-expense">{cny(dash.totalExpenseCny)}</p><p className="text-[10px] text-slate-400" data-testid="cd-dash-expense-usd">≈ {money(dash.totalExpense)}</p></div>
        <div className="bg-slate-50 rounded-lg px-3 py-2"><p className="text-[11px] text-slate-400">{zh ? "净利" : "Net"}</p><p className={`text-lg font-extrabold ${netClr}`} data-testid="cd-dash-net">{cny(dash.totalNetCny)}</p><p className="text-[10px] text-slate-400" data-testid="cd-dash-net-usd">≈ {money(dash.totalNet)}</p></div>
        <div className="bg-slate-50 rounded-lg px-3 py-2"><p className="text-[11px] text-slate-400">{zh ? "盈利/亏损月份" : "Profit/loss months"}</p><p className="text-lg font-extrabold text-slate-800" data-testid="cd-dash-months"><span className="text-teal-700">{dash.profitMonths}</span> / <span className="text-red-600">{dash.lossMonths}</span></p></div>
      </div>
      <ResponsiveContainer width="100%" height={240}>
        <ComposedChart data={dash.months} margin={{ top: 4, right: 8, left: -8, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
          <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(m) => m.slice(2)} />
          <YAxis tickFormatter={(v) => "¥" + (Math.abs(v) >= 1000 ? Math.round(v / 1000) + "k" : v)} tick={{ fontSize: 11, fill: "#64748b" }} />
          <Tooltip formatter={(v) => cny(v)} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar dataKey="incomeCny" name={zh ? "服务费收入 ¥" : "Income ¥"} fill="#0d9488" radius={[4, 4, 0, 0]} />
          <Bar dataKey="expenseCny" name={zh ? "运营成本 ¥" : "Cost ¥"} fill="#d97706" radius={[4, 4, 0, 0]} />
          <Line type="monotone" dataKey="netCny" name={zh ? "净利 ¥" : "Net ¥"} stroke="#4F6BFF" strokeWidth={2} dot={{ r: 2.5 }} />
        </ComposedChart>
      </ResponsiveContainer>
      {showBudget && <BudgetDialog zh={zh} api={api} cm={cm} onClose={() => setShowBudget(false)} onSaved={() => { setShowBudget(false); onSaved && onSaved(); }} />}
    </div>
  );
}

function BudgetBar({ zh, kind, label, value, limit, bad }) {
  const isCost = kind === "cost";
  const pct = limit > 0 ? Math.min(100, Math.round((value / limit) * 100)) : 0;
  const barClr = limit <= 0 ? "#cbd5e1" : bad ? "#dc2626" : isCost ? "#0d9488" : "#16a34a";
  return (
    <div className={`rounded-lg border px-3 py-2.5 ${bad ? "border-red-300 bg-red-50/50" : "border-slate-200 bg-slate-50/50"}`} data-testid={`cd-budget-bar-${kind}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] text-slate-500">{label}</p>
        {limit > 0 ? (
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${bad ? "bg-red-100 text-red-700 animate-pulse" : "bg-teal-100 text-teal-700"}`} data-testid={`cd-budget-badge-${kind}`}>
            {bad ? (isCost ? (zh ? "超支" : "Over") : (zh ? "未达标" : "Below")) : (isCost ? (zh ? "预算内" : "OK") : (zh ? "已达标" : "Met"))}
          </span>
        ) : (
          <span className="text-[10px] text-slate-400">{zh ? (isCost ? "未设预算" : "未设目标") : "not set"}</span>
        )}
      </div>
      <p className="text-sm font-bold text-slate-800 mt-1">
        {"¥" + Number(value || 0).toLocaleString()}
        {limit > 0 && <span className="text-[11px] font-medium text-slate-400"> / {"¥" + Number(limit).toLocaleString()}{isCost ? (zh ? " 预算" : "") : (zh ? " 目标" : "")}</span>}
      </p>
      <div className="h-1.5 rounded-full bg-slate-200 mt-1.5 overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${limit > 0 ? pct : 0}%`, background: barClr }} />
      </div>
    </div>
  );
}

function BudgetDialog({ zh, api, cm, onClose, onSaved }) {
  const [f, setF] = useState({ budget: cm.budget || "", target: cm.target || "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/finance/budgets", { branch: "cd", monthlyTotal: Number(f.budget) || 0, revenueTarget: Number(f.target) || 0 });
      toast.success(zh ? "成都预算与目标已保存（月初自动检查超支/未达标并提醒）" : "Budget & target saved");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="cd-budget-dialog">
        <DialogHeader>
          <DialogTitle>{zh ? "成都公司月度预算与目标（¥）" : "Chengdu Budget & Target (CNY)"}</DialogTitle>
          <DialogDescription>{zh ? "以人民币设定；每月1日自动检查上月：成本超预算或服务费未达标将提醒财务与管理层。" : "Set in CNY; checked monthly on the 1st; alerts finance & management."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <label className="text-xs font-medium text-slate-500 block">{zh ? "月度成本预算 (¥ 人民币)" : "Monthly cost budget (CNY)"}
            <input data-testid="cd-budget-input" type="number" min="0" className={inp} value={f.budget} onChange={(e) => setF((p) => ({ ...p, budget: e.target.value }))} placeholder="0 = 不检查" />
          </label>
          <label className="text-xs font-medium text-slate-500 block">{zh ? "月度服务费收入目标 (¥ 人民币)" : "Monthly fee income target (CNY)"}
            <input data-testid="cd-target-input" type="number" min="0" className={inp} value={f.target} onChange={(e) => setF((p) => ({ ...p, target: e.target.value }))} placeholder="0 = 不检查" />
          </label>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={onClose}>{zh ? "取消" : "Cancel"}</Button>
            <Button type="button" data-testid="cd-budget-save" disabled={busy} onClick={submit} className="bg-teal-600 hover:bg-teal-700">{busy ? (zh ? "保存中…" : "…") : (zh ? "保存" : "Save")}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FixedExpenseCard({ zh, api, canManage, onGenerated }) {
  const [d, setD] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get("/finance/cd-fixed-template").then(({ data }) => setD(data)).catch(() => setD({ items: [], opCategories: [], generatedThisMonth: false, monthlyTotal: 0, month: "" }));
  }, [api]);
  useEffect(() => { load(); }, [load]);
  const generate = async () => {
    if (!window.confirm(zh ? `一键生成 ${d.month} 成都固定支出（${d.items.length} 项，合计 ¥${Number(d.monthlyTotal).toLocaleString()}，按当日汇率约 $${Number(d.monthlyTotalUsd || 0).toLocaleString()}）？将计入成都运营成本。` : "Generate this month's fixed expenses?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/finance/cd-fixed-generate", {});
      toast.success(zh ? `已生成 ${data.month} 固定支出 ${data.created} 项，合计 ¥${Number(data.total).toLocaleString()}（折 $${Number(data.totalUsd || 0).toLocaleString()}）` : `Generated ${data.created} items`);
      load(); onGenerated && onGenerated();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };
  if (!d) return null;
  return (
    <div className="bg-white rounded-xl border border-slate-200 px-5 py-4" data-testid="cd-fixed-card">
      <div className="flex flex-wrap items-center gap-2">
        <Building2 className="h-4 w-4 text-teal-600" />
        <h3 className="font-semibold text-slate-900">{zh ? "成都公司每月固定支出（工资/房租等 · ¥）" : "Chengdu Fixed Monthly Costs (CNY)"}</h3>
        {d.items.length > 0 ? (
          <span className="text-xs text-slate-400">{d.items.length} {zh ? "项 · 每月合计" : "items ·"} <span className="font-bold text-slate-700" data-testid="cd-fixed-total">¥{Number(d.monthlyTotal).toLocaleString()}</span>{d.monthlyTotalUsd != null && <span className="text-slate-400"> ≈ ${Number(d.monthlyTotalUsd).toLocaleString()}</span>}</span>
        ) : (
          <span className="text-xs text-amber-600" data-testid="cd-fixed-empty">{zh ? "尚未设置模板" : "No template yet"}</span>
        )}
        {d.generatedThisMonth && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-teal-100 text-teal-700" data-testid="cd-fixed-done">{zh ? `${d.month} 已生成 ✓` : "Generated ✓"}</span>}
        {canManage && (
          <span className="ml-auto flex items-center gap-2">
            <button data-testid="cd-fixed-edit-btn" onClick={() => setShowEdit(true)}
              className="text-[11px] font-semibold px-2.5 py-1.5 rounded-md border border-slate-300 text-slate-600 hover:border-teal-500 hover:text-teal-600 transition-colors bg-white">
              {zh ? "编辑模板" : "Edit Template"}
            </button>
            <button data-testid="cd-fixed-generate-btn" onClick={generate} disabled={busy || d.generatedThisMonth || d.items.length === 0}
              className="text-[11px] font-semibold px-2.5 py-1.5 rounded-md bg-teal-600 hover:bg-teal-700 disabled:opacity-40 text-white transition-colors">
              {busy ? (zh ? "生成中…" : "…") : (zh ? "一键生成本月成本" : "Generate This Month")}
            </button>
          </span>
        )}
      </div>
      {d.items.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2.5" data-testid="cd-fixed-items">
          {d.items.map((it, i) => (
            <span key={i} className="text-[11px] px-2 py-1 rounded-full bg-slate-100 text-slate-700">{it.label} · ¥{Number(it.amount).toLocaleString()}</span>
          ))}
        </div>
      )}
      {showEdit && <FixedTemplateDialog zh={zh} api={api} d={d} onClose={() => setShowEdit(false)} onSaved={() => { setShowEdit(false); load(); }} />}
    </div>
  );
}

function FixedTemplateDialog({ zh, api, d, onClose, onSaved }) {
  const [rows, setRows] = useState(() => (d.items.length ? d.items.map((r) => ({ ...r })) : [
    { opCategory: "staff_salary", label: zh ? "员工工资" : "Salaries", amount: "" },
    { opCategory: "rent", label: zh ? "办公室租金" : "Rent", amount: "" },
  ]));
  const [busy, setBusy] = useState(false);
  const cats = d.opCategories || [];
  const set = (i, k) => (e) => setRows((p) => p.map((r, j) => (j === i ? { ...r, [k]: e.target.value } : r)));
  const save = async () => {
    const valid = rows.filter((r) => (r.label || "").trim() && Number(r.amount) > 0);
    if (!valid.length) return toast.error(zh ? "请至少填写一项（名称+金额）" : "At least one item");
    if (valid.some((r) => !r.opCategory)) return toast.error(zh ? "请为每项选择支出类别" : "Select category");
    setBusy(true);
    try {
      await api.put("/finance/cd-fixed-template", { items: valid.map((r) => ({ ...r, amount: Number(r.amount) })) });
      toast.success(zh ? "固定支出模板已保存" : "Template saved");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="cd-fixed-dialog">
        <DialogHeader>
          <DialogTitle>{zh ? "成都固定支出模板（¥）" : "Fixed Expense Template (CNY)"}</DialogTitle>
          <DialogDescription>{zh ? "工资、房租等每月固定成本，金额为人民币；每月点「一键生成」即批量记入成都运营费用（记账日期为当月1日，按当日汇率折算美元计入总表）。" : "Monthly fixed costs in CNY booked to Chengdu operating expenses; converted to USD at the 1st-of-month rate."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`cd-fixed-row-${i}`}>
              <select data-testid={`cd-fixed-cat-${i}`} className={inp + " w-44 shrink-0"} value={r.opCategory || ""} onChange={set(i, "opCategory")}>
                <option value="">{zh ? "支出类别" : "Category"}</option>
                {cats.map((c) => <option key={c.key} value={c.key}>{zh ? c.zh : c.en}</option>)}
              </select>
              <input data-testid={`cd-fixed-label-${i}`} className={inp + " flex-1"} value={r.label} onChange={set(i, "label")} placeholder={zh ? "名称（如 设计团队工资）" : "Label"} />
              <input data-testid={`cd-fixed-amount-${i}`} type="number" min="0" className={inp + " w-28 shrink-0"} value={r.amount} onChange={set(i, "amount")} placeholder="¥" />
              <button data-testid={`cd-fixed-del-${i}`} onClick={() => setRows((p) => p.filter((_, j) => j !== i))} className="text-slate-300 hover:text-red-600 shrink-0"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          <button data-testid="cd-fixed-add" onClick={() => setRows((p) => [...p, { opCategory: "", label: "", amount: "" }])}
            className="inline-flex items-center gap-1 text-xs font-semibold text-teal-600 hover:text-teal-700"><Plus className="h-3.5 w-3.5" />{zh ? "新增一项" : "Add item"}</button>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>{zh ? "取消" : "Cancel"}</Button>
            <Button type="button" data-testid="cd-fixed-save" disabled={busy} onClick={save} className="bg-teal-600 hover:bg-teal-700">{busy ? (zh ? "保存中…" : "…") : (zh ? "保存模板" : "Save")}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CategoryManager({ zh, api, categories, onClose, onSaved }) {
  const [rows, setRows] = useState(() => categories.map((c) => ({ ...c })));
  const [busy, setBusy] = useState(false);
  const set = (i, k) => (e) => setRows((p) => p.map((r, j) => (j === i ? { ...r, [k]: e.target.value } : r)));
  const save = async () => {
    if (!rows.some((r) => (r.zh || "").trim())) return toast.error(zh ? "至少保留一个服务类别" : "Keep at least one category");
    setBusy(true);
    try {
      await api.put("/finance/cd-service-categories", { categories: rows });
      toast.success(zh ? "服务类别已更新" : "Categories updated");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="icf-cats-dialog">
        <DialogHeader>
          <DialogTitle>{zh ? "管理服务类别" : "Manage Service Categories"}</DialogTitle>
          <DialogDescription>{zh ? "增删改内部服务类别。已有记录的类别删除后，历史记录仍保留原类别。" : "Add/edit/remove categories. Existing records keep their original category."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={r.key || `new-${i}`} className="flex items-center gap-2" data-testid={`icf-cat-row-${i}`}>
              <input data-testid={`icf-cat-zh-${i}`} className={inp + " flex-1"} value={r.zh} onChange={set(i, "zh")} placeholder={zh ? "中文名称" : "Chinese name"} />
              <input data-testid={`icf-cat-en-${i}`} className={inp + " flex-1"} value={r.en || ""} onChange={set(i, "en")} placeholder="English" />
              <button data-testid={`icf-cat-del-${i}`} onClick={() => setRows((p) => p.filter((_, j) => j !== i))} className="text-slate-300 hover:text-red-600 shrink-0"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          <button data-testid="icf-cat-add" onClick={() => setRows((p) => [...p, { zh: "", en: "" }])}
            className="inline-flex items-center gap-1 text-xs font-semibold text-teal-600 hover:text-teal-700"><Plus className="h-3.5 w-3.5" />{zh ? "新增类别" : "Add category"}</button>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>{zh ? "取消" : "Cancel"}</Button>
            <Button type="button" data-testid="icf-cats-save" disabled={busy} onClick={save} className="bg-teal-600 hover:bg-teal-700">{busy ? (zh ? "保存中…" : "Saving…") : (zh ? "保存" : "Save")}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function NewFeeDialog({ zh, api, payers, categories, onClose, onSaved }) {
  const [f, setF] = useState({ fromBranch: "", category: "", amount: "", date: new Date().toISOString().slice(0, 10), description: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const submit = async () => {
    if (!f.fromBranch) return toast.error(zh ? "请选择付费分公司" : "Select payer");
    if (!f.category) return toast.error(zh ? "请选择服务类别" : "Select category");
    if (!Number(f.amount) || Number(f.amount) <= 0) return toast.error(zh ? "金额必须大于 0" : "Amount must be > 0");
    setBusy(true);
    try {
      await api.post("/finance/intercompany-fees", { ...f, amount: Number(f.amount) });
      toast.success(zh ? "已录入并完成双向记账（付费方支出 + 成都收入）" : "Fee booked on both sides");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="icf-new-dialog">
        <DialogHeader>
          <DialogTitle>{zh ? "录入内部服务费" : "New Intercompany Fee"}</DialogTitle>
          <DialogDescription>{zh ? "自动双向记账：付费方运营支出（$）+ 成都服务收入（按当日汇率折 ¥ 入账）" : "Auto double-entry: payer expense ($) + Chengdu income (booked in CNY at that day's rate)"}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs font-medium text-slate-500">{zh ? "付费分公司" : "Payer branch"}
              <select data-testid="icf-form-payer" className={inp} value={f.fromBranch} onChange={set("fromBranch")}>
                <option value="">{zh ? "请选择" : "Select"}</option>
                {payers.map((p) => <option key={p.key} value={p.key}>{zh ? p.zh : p.en}</option>)}
              </select>
            </label>
            <label className="text-xs font-medium text-slate-500">{zh ? "服务类别" : "Category"}
              <select data-testid="icf-form-category" className={inp} value={f.category} onChange={set("category")}>
                <option value="">{zh ? "请选择" : "Select"}</option>
                {categories.map((c) => <option key={c.key} value={c.key}>{zh ? c.zh : c.en}</option>)}
              </select>
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs font-medium text-slate-500">{zh ? "金额 (USD)" : "Amount (USD)"}
              <input data-testid="icf-form-amount" type="number" min="0" className={inp} value={f.amount} onChange={set("amount")} placeholder="0" />
            </label>
            <label className="text-xs font-medium text-slate-500">{zh ? "日期" : "Date"}
              <input data-testid="icf-form-date" type="date" className={inp} value={f.date} onChange={set("date")} />
            </label>
          </div>
          <label className="text-xs font-medium text-slate-500 block">{zh ? "服务项目说明" : "Description"}
            <input data-testid="icf-form-desc" className={inp} value={f.description} onChange={set("description")} placeholder={zh ? "例：3月 ADU 方案绘图 12 套" : "e.g. 12 ADU plan sets in March"} />
          </label>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={onClose}>{zh ? "取消" : "Cancel"}</Button>
            <Button type="button" data-testid="icf-form-submit" disabled={busy} onClick={submit} className="bg-teal-600 hover:bg-teal-700">
              {busy ? (zh ? "保存中…" : "Saving…") : (zh ? "保存并记账" : "Save & Book")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
